import { Injectable } from '@angular/core';
import { MasterJsonKeyPrefix } from '@conf/asw-master.config';
import { SystemDateService } from '@lib/services';
import { CountryWithPosCountryForSmsSendTelNumber } from '../../master/country-with-pos-country-for-sms-send-tel-number';
import { CountryCountryI18nAllData } from '../../master/country-countryI18n-all';
import { passportCountryPulldown } from '../../interface/passport-country-pulldown';
import { CacheKeyValue } from '../../interface/checkin-passenger-input';
import { StateByStateCode } from '../../master/state-by-state-code';
import { GenderByGenderForCheckin } from '../../master/gender-by-gender-for-checkin';
import { MileageProgramI18NByCarrierCode } from '../../master/mileage-program-i18n-by-carrier-code';
import { MasterDataService } from '../master-data/master-data.service';
import { getDateString } from '../../helpers';
import { StaticMsgPipe } from '@lib/pipes';

@Injectable({
  providedIn: 'root',
})
export class CheckinPassengerInputCacheService {
  /** セパレータとなる選択肢の文言キー */
  private readonly SEPARATOR_STATIC_MESSAGE_KEY = `${MasterJsonKeyPrefix.STATIC}pulldown.separatorHyphen`;

  /** 「選択してください」選択肢 */
  private SEPARATOR_KEY_VALUE: passportCountryPulldown;

  constructor(
    private _systemDateService: SystemDateService,
    private _masterDataService: MasterDataService,
    private staticMsgPipe: StaticMsgPipe
  ) {
    this.SEPARATOR_KEY_VALUE = {
      key: '',
      value: this.staticMsgPipe.transform(this.SEPARATOR_STATIC_MESSAGE_KEY),
    };
  }

  /**
   * 国リストを取得する
   * @param posCountryCode POS国コード
   * @returns 国リスト
   */
  getCountriesKeyValue(posCountryCode: string): passportCountryPulldown[] {
    const country1 = this._masterDataService.getCountriesBelongToPosCountry(posCountryCode);
    const country2 = this._masterDataService.getCountriesNotBelongToPosCountryAndJapan(posCountryCode);
    const country = country1.concat(this.SEPARATOR_KEY_VALUE, country2);

    return country;
  }

  /**
   * 国リスト(UNO除く)を取得する
   * @param countries 国リスト
   * @returns 国リスト(UNO除く)
   */
  getCountriesWithoutUnoKeyValue(countries: passportCountryPulldown[]): passportCountryPulldown[] {
    return countries.filter((country) => country.key !== 'UNO');
  }

  /**
   * SMS送信用電話番号国リストを取得する
   * @param posCountryCode POS国コード
   * @param posCountrySms POS管轄国SMS送信用電話番号国オブジェクト
   * @param countryI18N 国リスト
   * @returns SMS送信用電話番号国リスト
   */
  getCountriesSmsKeyValue(
    posCountryCode: string,
    posCountrySms: CountryWithPosCountryForSmsSendTelNumber,
    countryI18N: CountryCountryI18nAllData[]
  ): CacheKeyValue[] {
    const posCounties = this.getPosCountriesSmsKeyValue(posCountryCode, posCountrySms, countryI18N);
    const noPosCountries = this.getNoPosCountriesSmsKeyValue(countryI18N, posCounties);

    return posCounties.concat(this.SEPARATOR_KEY_VALUE, noPosCountries);
  }

  /**
   * 国コード(2レター)をキーとする国オブジェクトを取得する  (国連だけ3レター)
   * @param countryI18N 国リスト
   * @returns 国オブジェクト
   */
  getCountryObject(countryI18N: CountryCountryI18nAllData[]) {
    const countryArray = countryI18N.map((country) => [
      country.country_2letter_code || country.country_3letter_code,
      { ...country },
    ]);
    const countryObject = Object.fromEntries(countryArray);

    return countryObject;
  }

  /**
   * 国コード(3レター)をキーとする国オブジェクトを取得する
   * @param countryI18N 国リスト
   * @returns 国オブジェクト
   */
  getCountryObjectBy3letterCode(countryI18N: CountryCountryI18nAllData[]) {
    const countryArray = countryI18N.map((country) => [country.country_3letter_code, { ...country }]);
    const countryObject = Object.fromEntries(countryArray);

    return countryObject;
  }

  /**
   * 国名静的文言キーを取得する
   * @param countryI18N 国リスト
   * @param countryCode 国コード
   * @returns 国名静的文言キー
   */
  getCountryKey(countryI18N: CountryCountryI18nAllData[], countryCode: string): string {
    return countryI18N.find((country) => country.country_2letter_code === countryCode)?.country_name ?? '';
  }

  getStateKeyValue(master: StateByStateCode): CacheKeyValue[] {
    return Object.keys(master)
      .map((key) => master[key][0])
      .map((master) => {
        return {
          key: master.value,
          value: master.display_content,
        };
      });
  }

  /**
   * 性別の選択肢を取得する
   * @param master 性別キャッシュ
   * @returns 男性・女性・その他の性別の選択肢リスト
   */
  getGenderTypeKeyValue(master: GenderByGenderForCheckin): CacheKeyValue[] {
    return Object.keys(master).map((key) => {
      return { key: master[key][0].value, value: master[key][0].display_content };
    });
  }

  /**
   * マイレージプログラムキャッシュオブジェクトから適用されるマイレージプログラム一覧を取得する
   * @param master マイレージプログラムキャッシュ
   * @param companyCode キャリアコード
   * @returns マイレージプログラムキャッシュオブジェクトから適用されるマイレージプログラム一覧
   */
  getMileageProgramKeyValue(master: MileageProgramI18NByCarrierCode, companyCode?: string): CacheKeyValue[] {
    const jpDate = +getDateString(this._systemDateService.getFormattedSystemDate());

    // マイレージプログラム名のリストを作成する
    const mileagePrograms = Object.entries(master)
      // 適用日範囲内に絞る
      .filter((entry) => entry[1].some((value) => +value.apply_from_date <= jpDate && +value.apply_to_date >= jpDate))
      // 先頭のマイレージプログラムを取得する
      .map(([_, mileagePrograms]) => mileagePrograms[0])
      .map((mileageProgram) => {
        return { key: mileageProgram.carrier_code, value: mileageProgram.mileage_program_name };
      });

    // キャリアコードを指定されてた場合はリスト先頭に移動する
    if (companyCode) {
      const companyCodeIndex = mileagePrograms.findIndex((mileageProgram) => mileageProgram.key === companyCode);
      if (companyCodeIndex > -1) {
        const removedMileageProgram = mileagePrograms.splice(companyCodeIndex, 1);
        mileagePrograms.splice(0, 0, removedMileageProgram[0]);
        mileagePrograms.splice(1, 0, this.SEPARATOR_KEY_VALUE);
      }
    }

    return mileagePrograms;
  }

  /**
   * POS国SMS送信用電話番号国リストを取得する
   * @param posCountryCode POS国コード
   * @param posCountrySms POS国SMS送信用電話番号国オブジェクト
   * @param countryI18N 国リスト
   * @returns POS国SMS送信用電話番号国リスト
   */
  private getPosCountriesSmsKeyValue(
    posCountryCode: string,
    posCountrySms: CountryWithPosCountryForSmsSendTelNumber,
    countryI18N: CountryCountryI18nAllData[]
  ): CacheKeyValue[] {
    const posCounties = posCountrySms[posCountryCode].map((posCountry) => {
      return {
        key: posCountry.country_2letter_code,
        value: posCountry.country_name,
      };
    });

    if (!posCounties.some((posCountry) => posCountry.key === 'JP')) {
      const jp = countryI18N.find((country) => country.country_2letter_code === 'JP');
      if (jp) {
        posCounties.push({
          key: jp.country_2letter_code,
          value: jp.country_name,
        });
      }
    }

    return posCounties;
  }

  /**
   * POS国以外SMS送信用電話番号国リストを取得する
   * @param countryI18N 国リスト
   * @param exceptCounties POS国以外SMS送信用電話番号国リストから省く国リスト
   * @returns POS国以外SMS送信用電話番号国リスト
   */
  private getNoPosCountriesSmsKeyValue(
    countryI18N: CountryCountryI18nAllData[],
    exceptCounties: CacheKeyValue[]
  ): CacheKeyValue[] {
    const noPosCountries: CacheKeyValue[] = [];
    countryI18N.forEach((country) => {
      if (
        !exceptCounties.some((exceptCountry) => exceptCountry.key === country.country_2letter_code) &&
        country.contact_tel_number_country_flg &&
        country.sms_send_flag
      ) {
        noPosCountries.push({
          key: country.country_2letter_code,
          value: country.country_name_sms,
        });
      }
    });

    return noPosCountries;
  }
}
